<template>
  <v-dialog v-model="open" max-width="600">
    <template v-slot:activator="{ props: activatorProps }">
      <v-btn 
        v-bind="activatorProps"
        color="primary"
        variant="outlined"
        prepend-icon="mdi-pencil"
      >
        Request Edit
      </v-btn>
    </template>

    <template v-slot:default="{ isActive }">
      <v-card class="rounded-lg">
        <v-card-title class="text-h5 pa-6 pb-4 d-flex align-center">
          <v-icon class="mr-3" color="primary" size="32">mdi-file-document-edit</v-icon>
          Request Record Edit
        </v-card-title>
        
        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-alert 
            type="info" 
            variant="tonal" 
            class="mb-6"
            density="compact"
          >
            <div class="text-body-2">
              <strong>Editing record for:</strong> {{ person.firstname }} {{ person.lastname }}
            </div>
          </v-alert>

          <v-row>
            <v-col cols="12">
              <div class="text-overline text-primary font-weight-bold mb-2">
                <v-icon size="small" class="mr-1">mdi-account</v-icon>
                Your Contact Information
              </div>
            </v-col>
            <v-col cols="12">
              <v-text-field 
                type="text" 
                v-model="data.contactname" 
                label="Your Name"
                prepend-inner-icon="mdi-account"
                variant="outlined"
                density="comfortable"
                required
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field 
                type="tel" 
                v-model="data.contactphone" 
                label="Phone Number"
                prepend-inner-icon="mdi-phone"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field 
                type="email" 
                v-model="data.contactemail" 
                label="Email Address"
                prepend-inner-icon="mdi-email"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
            <v-col cols="12">
              <div class="text-overline text-primary font-weight-bold mb-2 mt-2">
                <v-icon size="small" class="mr-1">mdi-text-box</v-icon>
                Your Request
              </div>
            </v-col>
            <v-col cols="12">
              <v-textarea 
                v-model="data.notes" 
                label="Describe the correction or update needed"
                prepend-inner-icon="mdi-message-text"
                variant="outlined"
                rows="4"
                required
                hint="Please provide as much detail as possible about the information that needs to be corrected"
                persistent-hint
              />
            </v-col>
            <v-col cols="12">
              <div class="text-overline text-primary font-weight-bold mb-2 mt-2">
                <v-icon size="small" class="mr-1">mdi-camera</v-icon>
                Gravesite Photo (Optional)
              </div>
            </v-col>
            <v-col cols="12">
              <v-alert 
                type="warning" 
                variant="tonal" 
                density="compact"
                class="mb-3"
              >
                <div class="text-body-2">
                  <strong>Important:</strong> Please upload photos of the gravesite only. 
                  Images not related to the gravesite may be rejected.
                </div>
              </v-alert>
              <v-file-input
                v-model="photoFile"
                label="Upload Gravesite Photo"
                prepend-inner-icon="mdi-camera"
                prepend-icon=""
                variant="outlined"
                density="comfortable"
                accept="image/jpeg,image/png,image/jpg"
                hint="JPG or PNG only, max 5MB. Photo will be reviewed before being displayed."
                persistent-hint
                :rules="[
                  v => !v || !v.length || v[0].size < 5000000 || 'Photo size should be less than 5 MB'
                ]"
              >
                <template v-slot:selection="{ fileNames }">
                  <v-chip
                    color="primary"
                    label
                    size="small"
                  >
                    {{ fileNames[0] }}
                  </v-chip>
                </template>
              </v-file-input>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn 
            variant="outlined"
            @click="isActive.value = false"
            :disabled="uploading"
          >
            Cancel
          </v-btn>
          <v-btn 
            color="primary"
            @click="addRequest()"
            prepend-icon="mdi-send"
            :disabled="!data.contactname || !data.notes || uploading"
            :loading="uploading"
          >
            Submit Request
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>

  <v-snackbar 
    v-model="success" 
    color="success"
    location="top"
    :timeout="5000"
  >
    <div class="d-flex align-center">
      <v-icon class="mr-2">mdi-check-circle</v-icon>
      <strong>Change Request Submitted Successfully!</strong>
    </div>
  </v-snackbar>
</template>

<script>

export default {
  name: 'AddChangeRequestDialog.vue',

  props:{
    person : Object
  },

  data: () => ({
    data : {
      peopleid : -1,
      cemeteryid : -1,
      contactname : "",
      contactphone : "",
      contactemail : "",
      notes : "",
    },
    open : false,
    success : false,
    photoFile: null,
    uploading: false
  }),

  created : function(){
    this.data.peopleid = this.person.id;
    this.data.cemeteryid = this.person.cemeteryid;
  },

  methods:{
    async addRequest(){
      this.uploading = true;

      try {
        console.log('PhotoFile:', this.photoFile);
        console.log('Is File?:', this.photoFile instanceof File);
        
        const formData = new FormData();
        formData.append('peopleid', this.data.peopleid);
        formData.append('cemeteryid', this.data.cemeteryid);
        formData.append('contactname', this.data.contactname);
        formData.append('contactphone', this.data.contactphone);
        formData.append('contactemail', this.data.contactemail);
        formData.append('notes', this.data.notes);
        
        if(this.photoFile instanceof File) {
          console.log('Appending file:', this.photoFile.name, 'Size:', this.photoFile.size);
          formData.append('image', this.photoFile, this.photoFile.name);
        } else {
          console.log('NO FILE TO APPEND');
        }
        
        // Log FormData contents
        for (let pair of formData.entries()) {
          console.log(pair[0] + ':', pair[1]);
        }

        const response = await fetch("https://app.cemeterycommunity.com:11000/api/addgenealogychangerequest", { 
          method: "POST",
          body: formData
        });

        const res = await response.json();
        
        if(res.results != "Failed"){
          this.open = false;
          this.success = true;
          
          this.data.contactname = "";
          this.data.contactphone = "";
          this.data.contactemail = "";
          this.data.notes = "";
          this.photoFile = null;
        }
        this.uploading = false;
      } catch(error) {
        console.error('Request submission failed:', error);
        this.uploading = false;
      }
    }
  }
}
</script>